import { DataField } from "@iventis/domain-model/model/dataField";
import { useMachine } from "@xstate/react";
import React, { BaseSyntheticEvent, FunctionComponent, useEffect, useState, useMemo } from "react";
import { ScheduleConfigVersionType } from "@iventis/domain-model/model/scheduleConfigVersionType";
import { Content } from "@iventis/translations/content/typed-content";
import { EMPTY_GUID, useObservableValue, xstateDevTools, getDataFieldListItems } from "@iventis/utilities";
import { ScheduleStatus } from "@iventis/domain-model/model/scheduleStatus";
import { Node } from "@iventis/domain-model/model/node";
import { BehaviorSubject } from "rxjs";
import { ScheduleVersion } from "@iventis/domain-model/model/scheduleVersion";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { FormWizardTemplate, Stages, AutocompleteWithLoading } from "@iventis/components";
import { hasPermissionsChanged } from "@iventis/permissions/src/permission-utilitites";
import { PermissionsEditActorComponent } from "@iventis/permissions/src/permissions-edit-wrappers";
import { Theme } from "@emotion/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TextField } from "@mui/material";
import { State } from "xstate";
import { InteractiveElement } from "@iventis/styles/src/components";
import { Header5, Body2 } from "@iventis/styles/src/components/texts";
import { muiInputFormsCSS, StyledInputLabel, styled } from "@iventis/styles";

import { scheduleApi } from "@iventis/api/src/api";

import { CreateSubScheduleContext, CreateSubScheduleEvents, SendEventToCreateSubSchedule, createSubScheduleMachine } from "./create-sub-schedule.machine";
import { CopyScheduleVersionComponent } from "./copy-schedule-versions";

import { ScheduleNode } from "./schedule-mock-data";
import { createPiblingNode, COPY_MACHINE_ID } from "./copy-schedule-versions.machine";
import { getScheduleVersionColour } from "./schedule-helper-functions";

export const CreateSubScheduleComponent: FunctionComponent<{
    close: () => void;
    parentSchedule: ScheduleNode;
    parentScheduleVersion: ScheduleVersion;
    scheduleId: string;
    scheduleVersionType: ScheduleConfigVersionType;
    dataField: DataField;
    addScheduleVersions: (scheduleVersions: ScheduleVersion[], copyPending: boolean) => void; // Once the schedule versions have been created, the parent can decide how to add the new schedule versions.
    piblings: Node[];
}> = ({ close, parentSchedule, parentScheduleVersion, scheduleId, scheduleVersionType, dataField, addScheduleVersions, piblings }) => {
    // initial schedule version so no dependencies
    const initialScheduleVersionObject = useMemo(
        () => ({
            id: EMPTY_GUID,
            type: scheduleVersionType,
            name: "",
            status: ScheduleStatus.Draft,
            dataFieldValues: [{ dataField, value: null }],
            parentId: parentSchedule.id,
            scheduleId,
            publishedAt: null,
        }),
        []
    );

    const [state, send] = useMachine(createSubScheduleMachine, {
        devTools: xstateDevTools,
        context: {
            parentSchedule,
            schedule: initialScheduleVersionObject,
            piblingSchedules: piblings.filter((p) => p.id !== parentSchedule.id).map((p) => createPiblingNode(p, false)),
        },
    });

    const translate = useIventisTranslate();
    const { originalPermissions, latestPermissions } = state.context.permissions;

    const isCopyMachineValid$ = useMemo(() => new BehaviorSubject<boolean>(false), []);
    const isCopyMachineValid = useObservableValue(isCopyMachineValid$);

    const [scheduleVersionsAdded, setScheduleVersionsAdded] = useState(false);

    useEffect(() => {
        if (!scheduleVersionsAdded && (state.matches("permissionsStep") || state.matches("finished"))) {
            addScheduleVersions(
                state.context.scheduleVersionsCreated.versions.filter(({ parentId }) => parentId !== null),
                state.context.scheduleVersionsCreated.copyPending
            );
            setScheduleVersionsAdded(true);
        }

        if (state.matches("finished")) {
            close();
        }
    }, [state.value]);

    const Stages: Stages = [
        {
            primaryButtonText: translate(Content.common.buttons.create),
            primaryButtonCallback: () => send("CREATE"),
            secondaryButtons: [{ buttonText: translate(Content.common.buttons.cancel), onButtonPressed: close }],
            isValid: state.matches("createStep.new.idle.valid") || (state.matches("createStep.copy.idle") && isCopyMachineValid),
            Component: (
                <CreateSubScheduleCreateStep
                    scheduleVersionType={scheduleVersionType}
                    state={state}
                    send={send}
                    parentSchedule={{ ...parentSchedule, colour: getScheduleVersionColour(parentScheduleVersion) }}
                    dataField={dataField}
                    isCopyMachineValid$={isCopyMachineValid$}
                />
            ),
        },
        {
            primaryButtonText: translate(Content.common.buttons.saveAndFinish),
            primaryButtonCallback: () =>
                hasPermissionsChanged(originalPermissions || [], latestPermissions || []) ? send([{ type: "SAVE" }, { type: "CLOSE" }]) : send({ type: "CLOSE" }),
            secondaryButtons: [{ buttonText: translate(Content.common.buttons.close), onButtonPressed: close }],
            isValid: state.context.isPermissionsIdle,
            Component: state.children.permissions != null && (
                <>
                    <PermissionsEditActorComponent parentState={state} />
                </>
            ),
        },
    ];

    const currentStage = state.matches("createStep") ? 0 : state.matches("permissionsStep") ? 1 : null;

    return (
        <>
            {currentStage != null && (
                <FormWizardTemplate
                    title={translate(Content.schedule.create_sub_schedule_form.create_new_sub_schedule, { subSchedule: dataField.name })}
                    stages={Stages}
                    currentStage={currentStage}
                >
                    {Stages[currentStage]?.Component}
                </FormWizardTemplate>
            )}
        </>
    );
};

const CreateSubScheduleCreateStep: FunctionComponent<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    state: State<CreateSubScheduleContext, CreateSubScheduleEvents, any, any, any>;
    send: SendEventToCreateSubSchedule;
    parentSchedule: ScheduleNode;
    dataField: DataField;
    scheduleVersionType: ScheduleConfigVersionType;
    isCopyMachineValid$: BehaviorSubject<boolean>;
}> = ({ state, send, parentSchedule, dataField, scheduleVersionType, isCopyMachineValid$ }) => {
    const translate = useIventisTranslate();
    const createNew = state.matches({ createStep: "new" });
    const copyExisting = state.matches({ createStep: "copy" });
    const { schedule } = state.context;
    const [dataFieldListItems, setDataFieldListItems] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        getDataFieldListItems(scheduleApi, dataField.id, []).then((listItems) => {
            setDataFieldListItems(listItems);
            setLoading(false);
        });
    }, []);

    return (
        <StyledContent>
            <StyledNavSelector>
                <InteractiveElement className={`nav-selector ${createNew && "nav-selected"}`} onClick={() => send("NEW")}>
                    {translate(Content.schedule.create_sub_schedule_form.new)}
                </InteractiveElement>
                <InteractiveElement className={`nav-selector ${copyExisting && "nav-selected"}`} onClick={() => send("COPY")}>
                    {translate(Content.schedule.create_sub_schedule_form.copy_existing)}
                </InteractiveElement>
                <div id="hr" />
            </StyledNavSelector>
            {createNew ? (
                <>
                    <Header5 style={{ marginTop: "20px" }}>{translate(Content.schedule.create_sub_schedule_form.parent_schedule)}</Header5>
                    <div className="parent-schedule">
                        <FontAwesomeIcon size="sm" icon={["fas", "circle"]} color={parentSchedule.colour} />
                        <Body2>{parentSchedule.name}</Body2>
                    </div>
                    <div className="inputs">
                        <div id="name-input">
                            <StyledInputLabel>
                                <Header5 id="name-label">{translate(Content.schedule.create_sub_schedule_form.name_of_calendar)}</Header5>
                            </StyledInputLabel>
                            <StyledNameInput
                                variant="outlined"
                                aria-labelledby="name-label"
                                name="calendar-name"
                                value={schedule.name}
                                inputProps={{
                                    "data-testid": "email-input",
                                }}
                                onChange={(e: BaseSyntheticEvent) => send("UPDATE_NAME", { payload: e.target.value })}
                            />
                        </div>
                        <div id="sport-input">
                            <StyledInputLabel>
                                <Header5>{dataField.name}</Header5>
                            </StyledInputLabel>
                            <AutocompleteWithLoading
                                value={schedule.dataFieldValues[schedule.dataFieldValues.findIndex((dfv) => dfv.dataField.id === dataField.id)].value}
                                loadingOptions={loading}
                                getOptionLabel={(option) => (option ? `${option.metaData.code} - ${option.name}` : "")}
                                options={dataFieldListItems}
                                onChange={(value: string) => send("UPDATE_DATA_FIELD", { payload: { dataFieldId: dataField.id, value } })}
                            />
                        </div>
                    </div>
                </>
            ) : copyExisting ? (
                <>
                    <Body2 style={{ margin: "20px 0" }}>
                        {translate(Content.schedule.create_sub_schedule_form.selectSubSchedulesToCopy, { scheduleVersionType: scheduleVersionType.name })}
                    </Body2>
                    {state.children[COPY_MACHINE_ID] && (
                        <CopyScheduleVersionComponent isCopyMachineValid$={isCopyMachineValid$} parentState={state} childType={state.context.schedule.type.name} />
                    )}
                </>
            ) : null}
        </StyledContent>
    );
};
const StyledContent = styled.div`
    display: flex;
    flex-direction: column;

    .parent-schedule {
        display: flex;
        margin-top: 10px;
        p {
            margin-left: 10px;
            margin-top: -2px;
        }
    }

    .inputs {
        display: flex;
        justify-content: stretch;
        gap: 20px;

        div {
            flex: 1 1 50%;
        }
    }

    ${muiInputFormsCSS}
`;

const StyledNavSelector = styled.nav`
    position: relative;
    .nav-selector {
        width: 50%;
        height: 44px;
        color: ${({ theme }: { theme: Theme }) => theme.typographyColors.subdued};
        position: relative;
        z-index: 10;
    }
    .nav-selected {
        color: ${({ theme }: { theme: Theme }) => theme.primaryColors.focusAccent};
        border-bottom: 2px solid ${({ theme }: { theme: Theme }) => theme.primaryColors.focusAccent};
    }
    #hr {
        position: absolute;
        bottom: 0;
        width: 100%;
        height: 2px;
        background-color: ${({ theme }: { theme: Theme }) => theme.typographyColors.subdued};
        opacity: 0.2;
    }
`;

const StyledNameInput = styled(TextField)`
    width: 100%;
`;
